// 広告自動化マンの状態を保存するストレージ層。
//
// 罠（GOAL.md / requirements.md より）:
// - ページ遷移で React state は消える前提 → 会話履歴・発話タイマー・抑制期限・
//   パネル開閉は sessionStorage に必ず持つ
// - 読み書きは全て try/catch（プライベートブラウジング等で例外を投げる環境がある）
//
// FIXES.md F7: 固定キーだと同一タブでのアカウント切替時に前の利用者の会話が
// 次の利用者に見え、次のリクエストの history としてそのまま送信されてしまう
// （商品名・クレジット残高・生成物タイトルを含む）。
// → 全キーを `adman.<userId>.<name>` で名前空間化し、ログアウト時は
//   `clearAllAdmanStorageForLogout()` で `adman.` 接頭辞のキーを丸ごと削除する
//   （utils/tokenManager.ts の clearAllAuthData から呼ばれる）。

import { AdmanHistoryMessage, AdmanRole } from '../../types/adman';

// 全キー共通の接頭辞。tokenManager.ts のログアウト処理もこの文字列を参照する。
export const ADMAN_STORAGE_PREFIX = 'adman.';

// ユーザーが未確定（ロード中等）のときのフォールバック用バケット。
// 実際のログイン利用者のデータと衝突しないよう、通常の数値IDとは区別できる文字列にしている。
const FALLBACK_USER_ID = 'anon';

export type AdmanStorageUserId = string | number | null | undefined;

const resolveUserId = (userId: AdmanStorageUserId): string => {
  if (userId === null || userId === undefined || userId === '') return FALLBACK_USER_ID;
  return String(userId);
};

const namespacedKey = (userId: AdmanStorageUserId, name: string): string =>
  `${ADMAN_STORAGE_PREFIX}${resolveUserId(userId)}.${name}`;

export const MAX_HISTORY = 10;

const ALLOWED_ROLES: AdmanRole[] = ['user', 'assistant'];

const isValidHistoryMessage = (value: unknown): value is AdmanHistoryMessage => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.content === 'string' &&
    ALLOWED_ROLES.includes(v.role as AdmanRole)
  );
};

const readSessionValue = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionValue = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // quota / プライベートブラウジング等は無視して既定動作にフォールバック
  }
};

const removeSessionValue = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// --- 会話履歴 ---

export const readHistory = (userId: AdmanStorageUserId): AdmanHistoryMessage[] => {
  const raw = readSessionValue(namespacedKey(userId, 'history'));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidHistoryMessage).slice(-MAX_HISTORY);
  } catch {
    return [];
  }
};

export const writeHistory = (userId: AdmanStorageUserId, history: AdmanHistoryMessage[]): void => {
  writeSessionValue(namespacedKey(userId, 'history'), JSON.stringify(history.slice(-MAX_HISTORY)));
};

export const appendHistory = (userId: AdmanStorageUserId, message: AdmanHistoryMessage): AdmanHistoryMessage[] => {
  const next = [...readHistory(userId), message].slice(-MAX_HISTORY);
  writeHistory(userId, next);
  return next;
};

export const clearHistory = (userId: AdmanStorageUserId): void => {
  removeSessionValue(namespacedKey(userId, 'history'));
};

// --- 発話タイマー ---

export const readLastSpokenAt = (userId: AdmanStorageUserId): number | null => {
  const raw = readSessionValue(namespacedKey(userId, 'lastSpokenAt'));
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const writeLastSpokenAt = (userId: AdmanStorageUserId, timestamp: number): void => {
  writeSessionValue(namespacedKey(userId, 'lastSpokenAt'), String(timestamp));
};

// --- 吹き出し抑制期限 ---

export const readBubbleSuppressUntil = (userId: AdmanStorageUserId): number | null => {
  const raw = readSessionValue(namespacedKey(userId, 'bubbleSuppressUntil'));
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const writeBubbleSuppressUntil = (userId: AdmanStorageUserId, timestamp: number): void => {
  writeSessionValue(namespacedKey(userId, 'bubbleSuppressUntil'), String(timestamp));
};

// --- パネル開閉 ---

export const readPanelOpen = (userId: AdmanStorageUserId): boolean => {
  return readSessionValue(namespacedKey(userId, 'panelOpen')) === '1';
};

export const writePanelOpen = (userId: AdmanStorageUserId, open: boolean): void => {
  writeSessionValue(namespacedKey(userId, 'panelOpen'), open ? '1' : '0');
};

// --- 手動選択中の商品ID（F20-b） ---
//
// 利用者がパネルの「他の商品にする」で選び直した商品ID。設定済みなら画面側の
// 自動検出（ページのcontext.product_ids）より優先される（useAdmanProductContext）。
// ページ遷移してもパネル自体はアンマウントされないため実際にはReact stateだけでも
// 持続するが、admanStorageの流儀（sessionStorage）に揃えてリロード耐性も持たせる。
// 「未設定」（自動に任せる）と「明示的に0件を選んだ」を区別するため、値は
// JSON配列そのものをキーの有無で判定する（キーが無ければ null = 自動）。

export const readManualProductIds = (userId: AdmanStorageUserId): number[] | null => {
  const raw = readSessionValue(namespacedKey(userId, 'manualProductIds'));
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v): v is number => typeof v === 'number');
  } catch {
    return null;
  }
};

export const writeManualProductIds = (userId: AdmanStorageUserId, ids: number[]): void => {
  writeSessionValue(namespacedKey(userId, 'manualProductIds'), JSON.stringify(ids));
};

export const clearManualProductIds = (userId: AdmanStorageUserId): void => {
  removeSessionValue(namespacedKey(userId, 'manualProductIds'));
};

// --- ログアウト時の一括削除（FIXES.md F7） ---

const clearPrefixedKeys = (storage: Storage): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(ADMAN_STORAGE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => {
      try {
        storage.removeItem(key);
      } catch {
        // ignore
      }
    });
  } catch {
    // storage自体にアクセスできない環境（プライベートブラウジング等）は無視
  }
};

// ログアウト時に全ユーザー分の adman.* キーを sessionStorage/localStorage から削除する。
// utils/tokenManager.ts の clearAllAuthData から呼ばれる想定（どのユーザーのキーかを
// 気にせず接頭辞だけで判定するので、userIdを知らなくても安全に呼べる）。
export const clearAllAdmanStorageForLogout = (): void => {
  if (typeof sessionStorage !== 'undefined') clearPrefixedKeys(sessionStorage);
  if (typeof localStorage !== 'undefined') clearPrefixedKeys(localStorage);
};
